import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { ScreenHeader } from '../../components/ScreenHeader'
import { SectionTitle } from '../../components/SectionTitle'
import { GameTitle, type GameId } from '../../components/GameTitle'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { RulesPdfViewer } from './RulesPdfViewer'
import {
  fetchRulesIndex, type ApiGame, type ApiRuleset, type RulesIndex,
} from './rulesApi'
import { useT } from '../../i18n'

interface HeaderProfile {
  display_name: string
  avatar_url: string | null
}

interface Props {
  profile?: HeaderProfile | null
  onProfileTap?: () => void
  /** Open the in-app living-rulebook reader for a game. */
  onOpenLivingBook: (args: {
    slug: string; title: string; jsonUrl: string; locales: string[]; accent: string
  }) => void
}

/**
 * Rules tab — driven by the NEW jordandraper.com rules API (the
 * rebrand site). Each game offers up to two documents, kept visibly
 * distinct per the integration spec:
 *
 *   PRINT RULEBOOK — the book in the box, a frozen PDF. Native opens
 *   the in-app cached viewer; web deep-links it.
 *
 *   LIVING RULEBOOK — the same rules as structured data, translated,
 *   updated in place. Opens the in-app reader (LivingRulebookScreen).
 *
 * Below those, the rulesets: every game/variant/crossover in the box,
 * with per-language PDFs. `official: false` files are unofficial
 * translations — rendered orange with "unofficial translation" in the
 * accessible name, per the spec (misrepresenting a volunteer's work as
 * ours is the failure mode).
 *
 * Content is never mirrored: PDFs and figures deep-link to the site's
 * storage, so a typo fix there is live here immediately.
 */
function openExternal(url: string): void {
  if (Capacitor.isNativePlatform()) {
    void Browser.open({ url })
  } else {
    window.open(url, '_blank', 'noopener')
  }
}

function isPdf(url: string): boolean {
  return url.toLowerCase().endsWith('.pdf')
}

/**
 * App-bundled box art + canonical title identity, keyed by API slug.
 * Accents come from the old hand-built catalog (which matched
 * jordandraper.com); Metro and Colorful use their boxed wordmark
 * treatments instead of a flat color. Capsule Hotel and Import/Export
 * were never in the old catalog, so their accents are sampled from the
 * box art and deepened for white backgrounds.
 */
interface SlugArt {
  image?: string
  accent?: string
  wordmark?: GameId
  /** Boxed expansion shown beside the base game (image + "+ TITLE"
   *  line), matching the live site's rules page. The expansion's
   *  rules live inside the base game's printed/living books. */
  expansion?: { title: string; image: string; accent: string }
}

const SLUG_ART: Record<string, SlugArt> = {
  'cactus':               { image: '/games/cactus.webp', accent: '#91CFAB' },
  'ceramic':              { image: '/games/ceramic.webp', accent: '#9A6A45' },
  'colorful':             { image: '/games/colorful.webp', accent: '#C9B785', wordmark: 'colorful' },
  'currency-sakana':      { image: '/games/currency-sakana.webp', accent: '#AD8B19' },
  'currency-torraku':     { image: '/games/currency-torraku.webp', accent: '#646464' },
  'id-eat-that':          { image: '/games/ideatthat.webp', accent: '#B58615', wordmark: 'ideatthat' },
  'import-export':        { image: '/games/import-export.webp', accent: '#7A7F9E' },
  'metal':                { image: '/games/metal.webp', accent: '#6B7078' },
  'praise':               { image: '/games/praise.webp', accent: '#F16B66' },
  'tokyo-capsule-hotel':  { image: '/games/capsule-hotel.webp', accent: '#00A8B5' },
  'tokyo-coin-laundry':   { image: '/games/coin-laundry.webp', accent: '#F16B66' },
  'tokyo-game-show':      { image: '/games/tokyo-game-show.webp', accent: '#7AB742' },
  'tokyo-jidohanbaiki':   { image: '/games/jidohanbaiki.webp', accent: '#DF1C6C' },
  'tokyo-jutaku':         { image: '/games/tokyo-jutaku.webp', accent: '#857F78' },
  'tokyo-metro':          { image: '/games/tokyo-metro.webp', accent: '#F79C20', wordmark: 'tokyo-metro',
    expansion: { title: 'Osaka Metro EXP', image: '/games/osaka-metro-exp.webp', accent: '#E88A4A' } },
  'three-step-solution':  { image: '/games/three-step.webp', accent: '#0EAE93', wordmark: 'three-step' },
  'tokyo-tsukiji-market': { image: '/games/tsukiji-market.webp', accent: '#126BB5',
    expansion: { title: 'Tokyo Tsukiji EXP', image: '/games/tsukiji-exp.webp', accent: '#68A69F' } },
  'turin-market':         { image: '/games/turin-market.webp', accent: '#DDBC74' },
  'washi-cats':           { image: '/games/washi-cats.webp', accent: '#E3B900' },
  'washi-cops':           { image: '/games/washi-cops.webp', accent: '#2FBBC9' },
}

const INK = '#111111'

/**
 * The site's section grouping, in its order. The API returns a flat
 * alphabetical list; readers know the catalog by family. Mixed Game
 * Rules has no section anymore by design — crossovers live inside
 * every game they involve, tagged CROSSOVER. Games the map doesn't
 * know yet land in Jordan Draper Games.
 */
/**
 * Expansions the API publishes as their own games. They belong to a base
 * game's card (SLUG_ART carries their art + title), so they must never
 * also appear as standalone entries in the library.
 */
const EXPANSION_SLUGS: ReadonlySet<string> = new Set([
  'osaka-metro-expansion',
  'tokyo-tsukiji-expansion',
])

const SECTIONS: { title: string; slugs: string[] }[] = [
  { title: 'Tokyo Series', slugs: [
    'tokyo-jidohanbaiki', 'tokyo-metro', 'tokyo-tsukiji-market',
    'currency-sakana', 'currency-torraku', 'tokyo-jutaku',
    'tokyo-coin-laundry', 'tokyo-capsule-hotel', 'tokyo-game-show',
  ]},
  { title: 'Tokyo Washi Games', slugs: ['washi-cats', 'washi-cops'] },
  { title: 'Material Series', slugs: ['metal', 'ceramic'] },
  { title: 'Japanese Designer Games', slugs: ['praise', 'colorful', 'cactus'] },
  { title: 'Jordan Draper Games', slugs: [
    'turin-market', 'import-export', 'id-eat-that', 'three-step-solution',
  ]},
]

/** White text on dark accents, ink on light ones (washi yellow etc.). */
function textOn(accentHex: string): string {
  const r = parseInt(accentHex.slice(1, 3), 16)
  const g = parseInt(accentHex.slice(3, 5), 16)
  const b = parseInt(accentHex.slice(5, 7), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? INK : '#FFFFFF'
}

export function RulesLibraryScreen({ profile, onProfileTap, onOpenLivingBook }: Props) {
  const t = useT()
  const [index, setIndex] = useState<RulesIndex | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [viewer, setViewer] = useState<{
    title: string; url: string; bgText: string; accent: string
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    fetchRulesIndex(attempt > 0)
      .then(idx => { if (!cancelled) setIndex(idx) })
      .catch(e => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [attempt])

  // PDFs open the in-app viewer on BOTH platforms now — the new rules
  // hosts serve with open CORS, so the web build can stream them into
  // pdf.js instead of punting to a browser tab (which some setups
  // treated as a download). Non-PDFs (videos etc.) still open outside.
  const openPdf = (title: string, url: string, bgText: string, accent: string) => {
    if (isPdf(url)) {
      setViewer({ title, url, bgText, accent })
    } else {
      openExternal(url)
    }
  }

  if (viewer) {
    return (
      <RulesPdfViewer
        title={viewer.title}
        url={viewer.url}
        bgText={viewer.bgText}
        bgColor={viewer.accent === INK ? undefined : viewer.accent}
        onBack={() => setViewer(null)}
        onOpenExternal={(u) => { setViewer(null); openExternal(u) }}
      />
    )
  }

  return (
    <div className="screen">
      <BackgroundTitle text="RULES" color="#B8C01F" />
      <ScreenHeader
        title={t('RULES')}
        profile={profile}
        onProfileTap={onProfileTap}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-28 relative z-10">
        <p className="text-sm text-brand-muted mb-4">{t('Every game in the Jordan Draper Games catalog. The print rulebook is the book in the box; the living rulebook is the same rules, readable in your language and always current.')}</p>

        {error && (
          <div className="card p-4">
            <p className="text-sm text-brand-muted mb-3">{t('The rules library couldn’t load. Check your connection and try again.')}</p>
            <button type="button" className="btn-primary w-full"
                    onClick={() => setAttempt(a => a + 1)}>
              {t('Retry')}
            </button>
          </div>
        )}

        {!index && !error && (
          <p className="text-sm text-brand-muted py-6 text-center">{t('Loading the library…')}</p>
        )}

        {index && SECTIONS.map(section => {
          const inSection = section.slugs
            .map(slug => index.games.find(g => g.slug === slug))
            .filter((g): g is ApiGame => Boolean(g))
          // Anything the map doesn't know yet joins the final section —
          // except the expansions, which are already shown on their base
          // game's card (see SLUG_ART's `expansion`). Without this they
          // ALSO land here as standalone cards at the bottom, which is
          // the duplication the main site dropped.
          const extras = section === SECTIONS[SECTIONS.length - 1]
            ? index.games.filter(g =>
                !SECTIONS.some(sec => sec.slugs.includes(g.slug))
                && !EXPANSION_SLUGS.has(g.slug))
            : []
          const games = [...inSection, ...extras]
          if (games.length === 0) return null
          return (
            <section key={section.title} className="mb-6">
              <SectionTitle className="mb-2">{t(section.title)}</SectionTitle>
              <div className="flex flex-col gap-3">
                {games.map(game => (
                  <GameCard
                    key={game.slug}
                    game={game}
                    onOpenPdf={openPdf}
                    onOpenLivingBook={onOpenLivingBook}
                  />
                ))}
              </div>
            </section>
          )
        })}

        {index && (
          <p className="text-xs text-brand-muted mb-4">{t('Live from the Jordan Draper Games rules library. Unofficial translations are shown in orange. Shared with permission.')}</p>
        )}
      </div>
    </div>
  )
}

function GameCard({ game, onOpenPdf, onOpenLivingBook }: {
  game: ApiGame
  onOpenPdf: (title: string, url: string, bgText: string, accent: string) => void
  onOpenLivingBook: Props['onOpenLivingBook']
}) {
  const t = useT()
  const art = SLUG_ART[game.slug] ?? {}
  const accent = art.accent ?? INK

  return (
    <div className="card p-4">
      <div className="flex items-center gap-3 mb-2">
        {art.image && (
          <img src={art.image} alt={game.title}
               className="w-14 h-14 rounded-2xl object-cover shadow-xs shrink-0" />
        )}
        {art.expansion && (
          <img src={art.expansion.image} alt={art.expansion.title}
               className="w-14 h-14 rounded-2xl object-cover shadow-xs shrink-0" />
        )}
        <div className="min-w-0">
          {art.wordmark ? (
            <GameTitle game={art.wordmark} size="sm" />
          ) : (
            <h3 className="font-display text-lg font-black uppercase tracking-widest leading-tight"
                style={{ color: accent }}>
              {game.title}
            </h3>
          )}
          {art.expansion && (
            <p className="font-display text-sm font-black uppercase tracking-widest leading-tight">
              <span className="text-brand-text">+ </span>
              <span style={{ color: art.expansion.accent }}>{art.expansion.title}</span>
            </p>
          )}
        </div>
      </div>

      {/* The two documents — visibly distinct, never interchangeable. */}
      {(game.printedRulebook || game.livingRulebook) && (
        <div className="flex flex-wrap gap-2 mb-1">
          {game.printedRulebook && (
            <button
              type="button"
              onClick={() => onOpenPdf(t('{title}: Print Rulebook', { title: game.title }), game.printedRulebook!.url, game.title, accent)}
              className="text-xs font-display font-bold rounded-lg px-3 py-2
                         active:scale-95 transition-transform"
              style={{ backgroundColor: `${accent}26`, color: INK }}
            >
              {t('Print Rulebook')}
            </button>
          )}
          {game.livingRulebook && (
            <button
              type="button"
              onClick={() => onOpenLivingBook({
                slug: game.slug,
                title: game.title,
                jsonUrl: game.livingRulebook!.json,
                locales: game.livingRulebook!.locales,
                accent,
              })}
              className="text-xs font-display font-bold rounded-lg px-3 py-2
                         active:scale-95 transition-transform"
              style={{ backgroundColor: accent, color: textOn(accent) }}
            >
              {t('Living Rulebook · {n} languages', { n: game.livingRulebook.locales.length })}
            </button>
          )}
        </div>
      )}

      {game.rulesets
        // A ruleset with no files and no video has nothing to offer a
        // tap (e.g. washi-cops' "Ice Thief!", whose file is lost
        // upstream and sits dormant in the index) — a bare title row
        // reads as broken UI, so skip until a document exists.
        .filter(rs => (rs.files?.length ?? 0) > 0 || rs.video)
        .map(rs => (
          <RulesetRow key={rs.id} ruleset={rs} gameTitle={game.title} accent={accent} onOpenPdf={onOpenPdf} />
        ))}
    </div>
  )
}

function RulesetRow({ ruleset, gameTitle, accent, onOpenPdf }: {
  ruleset: ApiRuleset
  gameTitle: string
  accent: string
  onOpenPdf: (title: string, url: string, bgText: string, accent: string) => void
}) {
  const t = useT()
  const isCrossover = (ruleset.games?.length ?? 0) > 1
  return (
    <div className="flex items-center justify-between gap-3 py-2.5
                    border-b border-brand-border/40 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-brand-text leading-snug">
          {ruleset.title}
          {isCrossover && <Tag>{t('Crossover')}</Tag>}
          {ruleset.onlineExclusive && <Tag>{t('Online')}</Tag>}
          {ruleset.yenChain && <Tag>{t('¥ chain')}</Tag>}
        </p>
        {ruleset.designers && ruleset.designers.length > 0 && (
          <p className="text-xs text-brand-muted truncate">{ruleset.designers.join(', ')}</p>
        )}
      </div>
      <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
        {ruleset.video && (
          <button
            type="button"
            onClick={() => openExternal(ruleset.video!)}
            className="text-xs font-display font-bold rounded-lg px-2.5 py-1.5
                       bg-brand-text text-white active:scale-95 transition-transform"
          >
            {t('Video')}
          </button>
        )}
        {(ruleset.files ?? []).map(f => (
          <button
            key={f.language + f.url}
            type="button"
            onClick={() => onOpenPdf(`${ruleset.title} (${f.languageName})`, f.url, gameTitle, accent)}
            aria-label={f.official
              ? `${ruleset.title}: ${f.languageName}`
              : t('{title}: {lang}, unofficial translation', { title: ruleset.title, lang: f.languageName })}
            title={f.official
              ? f.languageName
              : t('{lang}, unofficial translation', { lang: f.languageName })}
            className={`text-xs font-display font-bold rounded-lg px-2.5 py-1.5
                        active:scale-95 transition-transform
                        ${f.official ? '' : 'bg-orange-500/15 text-orange-700'}`}
            style={f.official ? { backgroundColor: `${accent}26`, color: INK } : undefined}
          >
            {f.language}
          </button>
        ))}
      </div>
    </div>
  )
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-1.5 inline-block align-middle text-[10px] font-semibold
                     uppercase tracking-wide text-brand-muted
                     bg-brand-elevated rounded px-1.5 py-0.5">
      {children}
    </span>
  )
}
